
import Vue from 'vue';
import axios from 'axios';
import router from '@/router/index';
import public_routes from '@/router/public';
import { backend_usecured_routes } from '@/utils/utils';
import { URL, api_max_retries, api_retry_delay_ms } from './globals';
import { getStore } from '@/store/registry';


const instance = axios.create({
    baseURL: URL
});

// Single-flight (2026-06-12): comparte UN solo POST /token/refresh/ entre todas
// las requests concurrentes que encuentren el token por vencer. Sin esto, una
// vista que dispara N llamadas en paralelo dentro de la ventana de refresh
// lanzaba N refreshes idénticos (ruido + presión al throttle token_refresh del
// backend). En fallo, la limpieza/redirect corre UNA sola vez aquí y el rechazo
// se propaga a todas las requests que esperaban.
let refresh_in_flight = null;

function refreshAccessToken() {
    if (!refresh_in_flight) {
        // #NOTE: Importante hacer la petición sin usar la instancia para evitar posible ciclo infinito si algo falla.
        refresh_in_flight = axios.post(URL + '/token/refresh/', {}, { withCredentials: true })
            .then(response => {
                // Update the token and the expiration time in the store.
                const token = response.data.access;
                getStore().commit('setAccessToken', token);
                return token;
            })
            .catch(error => {
                if (!public_routes.map(x => x.name).includes(router.currentRoute.name)) {
                    router.push({ name: "Login" });
                }
                getStore().commit('clearLoginData');
                return Promise.reject(error);
            })
            .finally(() => {
                // Liberar SIEMPRE el slot (éxito o fallo) para que el próximo
                // ciclo de expiración pueda crear un refresh nuevo.
                refresh_in_flight = null;
            });
    }
    return refresh_in_flight;
}

instance.interceptors.request.use(config => {
    // #NOTE: La siguiente linea no funciona todavia porque el home /
    // está disponible tanto para usuarios registrados o no.
    // public_routes.map(x => x.name).includes(router.currentRoute.name)

    // Si la ruta es pública en el backend o es para pedir cualquier token
    // no se chequea la validez del token actual.
    if (backend_usecured_routes.some(r => config.url.startsWith(r))) {
        return config;
    }
    // A continuación se valida si el token está por expirar y se refresca
    // antes de efectuar la petición.
    // SEC-3 fase 2: el refresh token vive en una cookie HttpOnly (withCredentials),
    // no en el state — siempre se intenta el silent refresh con body vacío; un 401
    // significa "sin cookie / cookie blacklisteada" y se cierra la sesión local.
    const expires_in = getStore().state.expires_at - Date.now();
    const time_untill_expiration = 1000 * 60 * 5; // 5 minutes
    if (expires_in < time_untill_expiration) {
        return refreshAccessToken().then(token => {
            // Retry the original request with the new token.
            config.headers['Authorization'] = 'Bearer ' + token;
            return config;
        });
    } else if (getStore().state.jwt_token != null) {
        // Esta condición es redundante! Se puede eliminar o no, da igual.
        config.headers['Authorization'] = 'Bearer ' + getStore().state.jwt_token;
    }
    return config;
});

// Sección, asignatura base, asignatura de perfil y perfil de egreso, con o sin alt-
const GRADED_EVIDENCE_DELETE_URL = /^\/(mesh\/(alt-)?egress-profile(-matter)?|teaching\/(alt-)?(matter|section))\/\d+\/$/;

function confirmGradedEvidenceLoss(message) {
    return Vue.swal.fire({
        type: "warning",
        title: "¿Eliminar de todas formas?",
        text: message,
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
    }).then(result => Boolean(result.value));
}

// Antes de borrar de la malla se consulta qué evaluaciones calificadas quedarían sin alcance y se
// pide confirmación, sin pasar por el 409. Va después del interceptor del token para correr antes que él.
instance.interceptors.request.use(async config => {
    const already_confirmed = config.params && config.params.confirm_graded_evidence_loss;
    if (config.method !== 'delete' || already_confirmed || !GRADED_EVIDENCE_DELETE_URL.test(config.url)) return config;
    const { data } = await instance.get(`${config.url}graded-evidence/`);
    if (!data.evaluations) return config;
    if (!(await confirmGradedEvidenceLoss(data.message))) throw new axios.Cancel('graded_evidence_cancelled');
    config.params = { ...(config.params || {}), confirm_graded_evidence_loss: true };
    return config;
});

const MAX_RETRIES = api_max_retries;
const RETRY_DELAY_MS = api_retry_delay_ms;

function shouldRetry(error) {
    if (!error.config) return false;
    // Solo casos donde el backend no procesó la request: caída de red o 502 del ALB.
    // Un 500 ya fue procesado y pudo persistir cambios; reintentarlo duplica operaciones.
    const isNetworkError = !error.response;
    const isBadGateway = error.response && error.response.status === 502;
    return isNetworkError || isBadGateway;
}

function retryRequest(error) {
    const config = error.config;
    config._retryCount = (config._retryCount || 0) + 1;
    const delay = RETRY_DELAY_MS * config._retryCount;
    console.warn(`Api retry ${config._retryCount}/${MAX_RETRIES}: ${config.url}`);
    return new Promise(resolve => setTimeout(resolve, delay)).then(() => instance(config));
}

const AUTH_ENDPOINTS = ['/token/refresh/', '/logout/', '/token/'];
const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

// Escritura de la aplicación (no de autenticación)
function isAppWrite(config) {
    const url = (config && config.url) || '';
    return !!config && WRITE_METHODS.includes(config.method) && !AUTH_ENDPOINTS.some(r => url.includes(r));
}

// Extract a human-readable message from a DRF 400 body without ever surfacing raw JSON:
// a coded { error, message } envelope (SEC-6: unsafe_content / html_not_allowed), a { detail }
// string, or the first field-error message. Returns null if nothing usable is found.
function firstErrorMessage(data) {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.detail) return data.detail;
    for (const key of Object.keys(data)) {
        const value = data[key];
        if (Array.isArray(value) && value.length && typeof value[0] === 'string') return value[0];
        if (typeof value === 'string') return value;
    }
    return null;
}

instance.interceptors.response.use(response => {
    return Promise.resolve(response);
}, error => {
    // Borrado cancelado en la confirmación: la petición nunca salió
    if (axios.isCancel(error)) return Promise.reject(error);
    console.log('Api interceptor error: ', error);

    if (shouldRetry(error) && (error.config._retryCount || 0) < MAX_RETRIES) {
        return retryRequest(error);
    }

    if (error.response) {
        const data = error.response.data;
        if (error.response.status === 409 && data && data.error === 'graded_evidence') {
            // Resguardo: borrado que no pasó por la consulta previa; si se confirma, se repite
            return confirmGradedEvidenceLoss(data.message).then(confirmed => {
                if (!confirmed) return Promise.reject(error);
                const config = error.config;
                config.params = { ...(config.params || {}), confirm_graded_evidence_loss: true };
                return instance(config);
            });
        }
        if (error.response.status === 401) {
            // Auth endpoints (login, refresh, logout) handle their own 401 —
            // don't redirect or the user gets kicked off the Login page on a
            // wrong-password attempt.
            const url = (error.config && error.config.url) || '';
            const isAuthEndpoint = AUTH_ENDPOINTS.some(r => url.includes(r));
            if (!isAuthEndpoint) {
                if (!getStore().getters.isLoged)
                    router.push({ name: 'Welcome' });
                else router.push({ name: 'DashboardView' })
            }
        } else if (error.response.status === 403) {
            Vue.swal.fire({
                title: "No tiene permiso para realizar esa acción",
                text: firstErrorMessage(error.response.data),
            }).then(() => {
                const home_route = getStore().getters.isLoged ? 'DashboardView' : 'Welcome';
                if (router.currentRoute.name !== home_route) {
                    router.push({ name: home_route });
                }
            });
        } else if (error.response.status === 400) {
            // Auth endpoints handle their own errors (expired cookie, etc.) — don't show
            // the generic "save failed" modal for them.
            const url = (error.config && error.config.url) || '';
            const isAuthEndpoint = AUTH_ENDPOINTS.some(r => url.includes(r));
            if (!isAuthEndpoint) {
                // Show a clean human message (coded { error, message }, { detail }, or first field error)
                // instead of dumping the raw JSON body.
                Vue.swal.fire({
                    icon: "warning",
                    title: "No se pudo guardar",
                    text: firstErrorMessage(error.response.data) || "Revise los datos e intente nuevamente.",
                });
            }
        } else if (error.response.status === 485) {
            // SEC-5: token revoked server-side (must_relog) — end this session/tab.
            getStore().commit('clearLoginData');
            if (router.currentRoute.name !== 'Welcome') {
                router.push({ name: 'Welcome' });
            }
        } else if (error.response.status === 502) {
            // El 502 del ALB queda solo en consola, sin modal: es frecuente y el usuario no
            // puede hacer nada distinto al reintento que shouldRetry ya agotó.
            console.warn(`Api 502 tras ${MAX_RETRIES} reintentos: ${error.config && error.config.url}`);
        } else if (error.response.status >= 500) {
            // El resto de los 5xx no tenía aviso: las acciones Vuex sólo encadenan .then(), así
            // que la promesa nunca se resolvía y el fallo quedaba invisible (el usuario perdía
            // lo editado sin enterarse).
            Vue.swal.fire({
                icon: "error",
                title: "No se pudo completar la operación",
                text: "Hubo un problema al comunicarse con el servidor. Revise su conexión e intente nuevamente.",
            });
        }
    } else if (isAppWrite(error.config)) {
        // Sin respuesta tras los reintentos (red caída o bloqueo de la WAF): la escritura no se guardó
        Vue.swal.fire({
            type: "error",
            title: "Sin conexión con el servidor",
            text: "Lo que acaba de hacer no quedó guardado. Espere un momento e intente nuevamente.",
        });
    }
    return Promise.reject(error);
});

export default instance;
